import 'cordova-plugin-purchase';

import _ from 'lodash';
import { Router } from 'vue-router';

import { tracking } from '@/helpers/track-event';
import logger from '@/infra/logger';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { usePurchaseStore } from '@/store/purchase';
import { AppPlatform } from '@/types/app';
import { EventName } from '@/types/events';
import { AvailableSubscription } from '@/types/purchase';

import notify from './notify';

let registered = false;
let successCount = 0;
let errorCount = 0;

export default async function ({
  CdvPurchaseStore,
  router,
  platform,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
  router: Router;
  platform: AppPlatform;
}) {
  const purchaseStore = usePurchaseStore();
  const availableSubscriptions = purchaseStore.availableSubscriptions;

  const { LogLevel } = window.CdvPurchase;
  CdvPurchaseStore.verbosity = LogLevel.DEBUG;

  await registerInAppPurchase({
    CdvPurchaseStore,
    subscriptions: availableSubscriptions,
    platform,
  });

  handleSubscriptionEvents({ CdvPurchaseStore, router });

  handleInAppPurchaseError({ CdvPurchaseStore });

  updateStoreProducts({
    CdvPurchaseStore,
    subscriptions: availableSubscriptions,
  });
}

async function registerInAppPurchase({
  CdvPurchaseStore,
  subscriptions,
  platform,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
  subscriptions: AvailableSubscription[];
  platform: AppPlatform;
}) {
  if (registered) {
    return;
  }

  const { Platform } = window.CdvPurchase;

  CdvPurchaseStore.register(subscriptions);

  if (platform === AppPlatform.Android) {
    CdvPurchaseStore.initialize([Platform.GOOGLE_PLAY]);
  } else {
    await CdvPurchaseStore.initialize([Platform.APPLE_APPSTORE]);
  }

  registered = true;
}

async function handleSubscriptionEvents({
  CdvPurchaseStore,
  router,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
  router: Router;
}) {
  CdvPurchaseStore.when().approved((transaction) => {
    const monitor = CdvPurchaseStore.monitor(
      transaction,
      (state) => {
        if (state === 'finished') {
          monitor.stop();
        }
      },
      '',
    );

    transaction.verify();
  });

  CdvPurchaseStore.when().verified((receipt) => {
    handleVerifiedPurchase({
      CdvPurchaseStore,
      receipt,
      router,
    });
  });
}

async function handleVerifiedPurchase({
  receipt,
  router,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
  receipt: CdvPurchase.VerifiedReceipt;
  router: Router;
}) {
  const authStore = useAuthStore();

  const isAuthenticated = Boolean(authStore.token);

  tracking.trackEvent(EventName.PurchaseVerified, {
    receipt,
    isAuthenticated,
  });

  await receipt.finish();

  if (!isAuthenticated) {
    return;
  }

  const purchaseStore = usePurchaseStore();
  const purchaseStatus = authStore.user.purchase?.status;

  if (purchaseStatus === 'consumed') {
    tracking.trackEvent(EventName.PurchaseAlreadyConsumed, {
      receipt,
    });

    return;
  }

  try {
    const receiptToken = getReceiptToken(receipt);

    if (!receiptToken) {
      throw new Error('Receipt not found');
    }

    const latestTransaction = getLatestTransaction(receipt);

    const { productId, transactionId } = getTransactionDetails({
      transaction: latestTransaction,
    });

    const { name, whatsapp, paymentMethod, totalValue } =
      getPurchaseDetails(productId);

    tracking.trackEvent(EventName.Purchase, {
      product_id: productId,
      transaction_id: transactionId,
      name,
      whatsapp,
      payment_method: paymentMethod,
      total_value: totalValue,
    });

    await purchaseStore.create({
      receipt: receiptToken,
      transactionId,
      productId,
    });

    handlePurchaseSuccess({ router });
  } catch (error) {
    handlePurchaseError(error);
  } finally {
    const appStore = useAppStore();
    appStore.setShowPaywall(false);
  }
}

function getReceiptToken(receipt: CdvPurchase.VerifiedReceipt) {
  if (!receipt) {
    return null;
  }

  return _.get(
    receipt,
    'sourceReceipt.nativeData.appStoreReceipt',
    _.get(receipt, 'sourceReceipt.purchaseToken', ''),
  );
}

function getLatestTransaction(receipt: CdvPurchase.VerifiedReceipt) {
  const transactions = receipt?.sourceReceipt?.transactions;

  if (!transactions || transactions.length === 0) {
    return null;
  }

  let latestTransaction = transactions.find((t) => t.purchaseDate);

  for (const t of transactions) {
    if (!t.purchaseDate) {
      continue;
    }

    if (
      (!latestTransaction?.purchaseDate && t.purchaseDate) ||
      (latestTransaction?.purchaseDate &&
        new Date(t.purchaseDate) > new Date(latestTransaction.purchaseDate))
    ) {
      latestTransaction = t;
    }
  }

  return latestTransaction;
}

function getTransactionDetails({
  transaction,
}: {
  transaction: CdvPurchase.Transaction | null | undefined;
}) {
  if (!transaction) {
    return { productId: '', transactionId: '' };
  }

  const productId = transaction.products[0].id;
  const transactionId = transaction.transactionId;

  return { productId, transactionId };
}

function getPurchaseDetails(productId: string) {
  const authStore = useAuthStore();

  const name = authStore.user.fullName;
  const whatsapp = authStore.user.whatsapp;

  const appStore = useAppStore();
  const platform = appStore.platform;

  const paymentMethod = `iap_${platform}`;

  const purchaseStore = usePurchaseStore();
  const products = purchaseStore.products ?? [];

  const product = products.find((product) => product.id === productId);

  let totalValue;

  if (product) {
    const price = Number(product.price) || 0;
    totalValue = String(price / 10 ** 6);
  }

  return { name, whatsapp, paymentMethod, totalValue };
}

function handlePurchaseSuccess({ router }: { router: Router }) {
  const authStore = useAuthStore();

  if (successCount > 0 || authStore.user.profile.filled) {
    return;
  }

  tracking.trackEvent(EventName.SuccessInAppPurchase);
  const fullscreenLoaderTexts = [
    'Confirmando pagamento',
    'Ajustando os últimos detalhes',
    'Quase tudo pronto para começar',
  ];
  const appStore = useAppStore();
  appStore.setFullscreenLoading({
    show: true,
    texts: fullscreenLoaderTexts,
    color: 'brand-04',
    theme: 'light',
  });

  setTimeout(() => {
    appStore.setShowNavigation(true);
    appStore.setFullscreenLoading({
      theme: 'dark',
      show: false,
      texts: [],
    });

    router.replace('/onboarding/post-purchase');
    successCount++;
  }, 5000);
}

function handlePurchaseError(error: any) {
  logger.captureException(error);
  notify({
    message: 'Não foi possível concluir sua compra',
    type: 'error',
    duration: 4000,
  });
}

function handleInAppPurchaseError({
  CdvPurchaseStore,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
}) {
  CdvPurchaseStore.error((error) => {
    logger.captureException(error);

    if (errorCount === 0) {
      const appStore = useAppStore();
      const purchaseStore = usePurchaseStore();
      purchaseStore.setLoadingPaywall(false);
      appStore.setShowPaywall(false);
      appStore.setFullscreenLoading({
        show: false,
        texts: [],
      });

      // Show BILLING_UNAVAILABLE error
      if (error.code === 6_777_001) {
        alert(error.message);
      } else {
        alert('Erro na compra');
      }
    }

    errorCount++;
  });
}

export function updateStoreProducts({
  CdvPurchaseStore,
  subscriptions,
  retryCount = 0,
}: {
  CdvPurchaseStore: InstanceType<typeof window.CdvPurchase.Store>;
  subscriptions: AvailableSubscription[];
  retryCount?: number;
}) {
  const products = subscriptions.map((subscription) => {
    const product = CdvPurchaseStore.get(
      subscription.id,
      subscription.platform,
    );

    if (!product) {
      if (retryCount < 3) {
        setTimeout(() => {
          updateStoreProducts({
            CdvPurchaseStore,
            subscriptions,
            retryCount: retryCount + 1,
          });
        }, 4000);
      }

      return null;
    }

    const price = product?.offers[0]?.pricingPhases[0]?.price;
    const offer = product.getOffer();

    if (!offer) {
      return null;
    }

    return {
      ...subscription,
      price,
      offer,
    };
  });

  const purchaseStore = usePurchaseStore();
  purchaseStore.setProducts(_.compact(products));
  purchaseStore.setIsIAPWorking(true);
}

export function getSuccessCount() {
  return successCount;
}
